'use client';

import { CoreEvent, eventNames } from '@carvana/core-events';
import Button from '@carvana/ds-button';
import cx from 'classnames';
import { useCallback, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';

type OwnProps = {
  displayText?: string | null;
  inputPlaceholder: string;
  page: 'home' | 'srp' | 'vdp';
  question?: string;
  slot?: React.ReactNode;
  slotEdgesVisible?: boolean;
};

const SEND_ICON_PATH =
  'M7.29289 0.292893C7.68342 -0.0976311 8.31658 -0.0976311 8.70711 0.292893L15.7071 7.29289C16.0976 7.68342 16.0976 8.31658 15.7071 8.70711C15.3166 9.09763 14.6834 9.09763 14.2929 8.70711L9 3.41421V15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15V3.41421L1.70711 8.70711C1.31658 9.09763 0.683418 9.09763 0.292893 8.70711C-0.0976311 8.31658 -0.0976311 7.68342 0.292893 7.29289L7.29289 0.292893Z';

export default function Composer({
  displayText,
  inputPlaceholder,
  page,
  question,
  slot,
  slotEdgesVisible = false
}: OwnProps) {
  const { isPending, whenReady } = useComposerOpenReady();
  const isTyping = displayText !== null && displayText !== undefined;
  const justSwipedRef = useRef(false);

  const handleOpen = useCallback(() => {
    if (isTyping) return;

    whenReady(() => {
      CoreEvent.emit(eventNames.composer.open, { question, source: 'page' });
    });
  }, [isTyping, question, whenReady]);

  const handleSwipeUp = useCallback(() => {
    justSwipedRef.current = true;
    handleOpen();

    // The native click that can follow a swipe fires in the same tick as
    // mouseup, before this timeout runs — so it's still suppressed below.
    // Expiring the flag here (rather than only on click) stops it from
    // silently swallowing an unrelated tap much later, after the composer
    // has since closed and reopened.
    setTimeout(() => {
      justSwipedRef.current = false;
    }, 0);
  }, [handleOpen]);

  // A mouse drag that starts and ends over the button also fires a native
  // click, which would otherwise double-emit the open event alongside the
  // swipe above.
  const handleButtonClick = useCallback(() => {
    if (justSwipedRef.current) {
      justSwipedRef.current = false;

      return;
    }

    handleOpen();
  }, [handleOpen]);

  const swipeHandlers = useSwipeable({
    onSwipedUp: handleSwipeUp,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    trackTouch: true
  });

  return (
    <div
      className="flex flex-col gap-lg rounded-t-2xl bg-background-weak p-2xl pt-0 shadow-[0px_-16px_40px_0px_rgba(13,55,94,0.08),0px_-4px_12px_0px_rgba(13,55,94,0.12)]"
      data-testid="composer"
      {...swipeHandlers}
    >
      <div
        aria-hidden="true"
        className="flex items-center justify-center pt-md"
        data-testid="composer-handle"
      >
        <span className="h-[4px] w-[40px] rounded-md bg-background-muted-weak" />
      </div>
      {slot && (
        <div
          className={cx(
            'no-scrollbar flex min-h-[36px] overflow-x-auto',
            slotEdgesVisible ? '-mx-2xl px-2xl' : 'w-full'
          )}
          data-testid="composer-slot-row"
        >
          <div className="flex w-full items-center gap-md">{slot}</div>
        </div>
      )}
      <Button
        aria-label="Open composer"
        className="flex w-full items-center gap-md rounded-full bg-background-default py-md pl-xl pr-md text-left shadow-DEFAULT"
        data-analytics-id="composer_shell_opened"
        data-analytics-attributes={JSON.stringify({ page })}
        data-testid="composer-open"
        disabled={isTyping}
        isLoading={isPending}
        onClick={handleButtonClick}
        variant="tertiary"
      >
        <span className="flex-1 truncate text-text-weak t-body-md">
          {displayText || inputPlaceholder}
        </span>
        <span className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center rounded-full bg-background-primary text-text-inverse">
          <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
            <path d={SEND_ICON_PATH} fill="currentColor" />
          </svg>
        </span>
      </Button>
    </div>
  );
}
